// Redimensiona imágenes en el navegador antes de subirlas al bucket.
//
// Motivo: las cámaras y capturas de pantalla producen archivos de 3-12 MB que
// se muestran en la revista a un ancho de pantalla. Reducirlos a 1920 px y
// recomprimirlos baja el peso un orden de magnitud sin diferencia visible.

export const MAX_ANCHO = 1920;
export const MAX_ALTO = 1920;
export const CALIDAD_JPEG = 0.85;

// Formatos que NO se tocan: el GIF perdería la animación al pasar por canvas
// y el SVG es vectorial (ya pesa poco y reescalarlo lo empeoraría).
const FORMATOS_INTOCABLES = ['image/gif', 'image/svg+xml'];

export const EXT_POR_MIME = {
  'image/jpeg': 'jpg',
  'image/jpg': 'jpg',
  'image/png': 'png',
  'image/webp': 'webp',
  'image/gif': 'gif',
  'image/svg+xml': 'svg',
};

const cargarImagen = (blob) =>
  new Promise((resolve, reject) => {
    const url = URL.createObjectURL(blob);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('No se pudo leer la imagen'));
    };
    img.src = url;
  });

// El JPEG no soporta transparencia. Si la imagen la usa hay que mantenerla en
// PNG, o los fondos transparentes saldrían negros.
const tieneTransparencia = (canvas, ctx) => {
  try {
    const { data } = ctx.getImageData(0, 0, canvas.width, canvas.height);
    // Se muestrea 1 de cada 10 píxeles: basta para detectar transparencia real
    // y evita recorrer millones de valores.
    for (let i = 3; i < data.length; i += 40) {
      if (data[i] < 250) return true;
    }
    return false;
  } catch (e) {
    // Si el canvas quedara "tainted" por alguna razón, se asume lo seguro.
    return true;
  }
};

const aBlob = (canvas, mime, calidad) =>
  new Promise((resolve) => canvas.toBlob(resolve, mime, calidad));

/**
 * Devuelve { blob, mime, ext } listo para subir.
 * Si redimensionar no aporta nada, devuelve el original sin tocar.
 */
export const optimizarImagen = async (archivo) => {
  const mimeOriginal = archivo.type || 'image/jpeg';
  const original = {
    blob: archivo,
    mime: mimeOriginal,
    ext: EXT_POR_MIME[mimeOriginal] || 'bin',
  };

  if (FORMATOS_INTOCABLES.includes(mimeOriginal)) return original;

  try {
    const img = await cargarImagen(archivo);

    // Solo se reduce, nunca se amplía.
    const escala = Math.min(1, MAX_ANCHO / img.width, MAX_ALTO / img.height);
    const ancho = Math.round(img.width * escala);
    const alto = Math.round(img.height * escala);

    const canvas = document.createElement('canvas');
    canvas.width = ancho;
    canvas.height = alto;
    const ctx = canvas.getContext('2d');
    ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(img, 0, 0, ancho, alto);

    const conservarPng = mimeOriginal === 'image/png' && tieneTransparencia(canvas, ctx);
    const mimeSalida = conservarPng ? 'image/png' : 'image/jpeg';

    const blob = await aBlob(canvas, mimeSalida, CALIDAD_JPEG);
    if (!blob) return original;

    // Si el "optimizado" no es más pequeño (imágenes ya comprimidas, iconos
    // diminutos), se conserva el archivo original.
    if (blob.size >= archivo.size) return original;

    return { blob, mime: mimeSalida, ext: EXT_POR_MIME[mimeSalida] };
  } catch (e) {
    console.warn('No se pudo optimizar la imagen, se sube el original:', e);
    return original;
  }
};
